import './ReturnsValueDetails.css'

import classNames from 'classnames'
import InfoElement from 'components/InfoElement'
import React, { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { emptyArray, formatNumberWithCurrency, getDifferenceWithPrecision } from 'utils'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'

import type { DetailsProps } from './types'
import useStyles from './useStyles'
import { getMissingDataText } from './utils'

function ReturnsValueDetails({ data, description, headers = emptyArray }: DetailsProps) {
  const { t: translate } = useTranslation()
  const classes = useStyles()

  if (!data || Object.keys(data).length === 0) {
    return null
  }

  const stationaryData = [
    {
      icon: <i className="fm fm-shop" />,
      count: data.storesCurrentNECom,
      countActive: data.storesCurrentNEComActive,
      revenue: data.revenueCurrentNECom,
      revenueCompared: data.revenueComparedNECom,
      revenuePercent: data.returnsValueShareCurrentNECom,
      returns: data.returnsValueCurrentNECom,
      revenuePerStore: data.revenuePerStoreCurrentNECom,
      missingDataText: 'revenue.noOpenStationaryStores',
    },
    {
      icon: <i className="fm fm-l4l" />,
      count: data.storesCurrentL4L,
      countActive: data.storesCurrentL4LActive,
      revenue: data.revenueCurrentL4L,
      revenueCompared: data.revenueComparedL4L,
      revenuePercent: data.returnsValueShareCurrentL4L,
      returns: data.returnsValueCurrentL4L,
      revenuePerStore: data.revenuePerStoreCurrentL4L,
      missingDataText: 'revenue.noComparableStoresOpen',
    },
    {
      icon: <i className="fm fm-surfchange-up" />,
      count: data.storesCurrentFSI,
      countActive: data.storesCurrentFSIActive,
      revenue: data.revenueCurrentFSI,
      revenueCompared: data.revenueComparedFSI,
      revenuePercent: data.returnsValueShareCurrentFSI,
      returns: data.returnsValueCurrentFSI,
      revenuePerStore: data.revenuePerStoreCurrentFSI,
      missingDataText: 'revenue.noOpenStoresWithAChangeOfArea',
    },
    {
      icon: <i className="fm fm-surfchange-down" />,
      count: data.storesCurrentFSD,
      countActive: data.storesCurrentFSDActive,
      revenue: data.revenueCurrentFSD,
      revenueCompared: data.revenueComparedFSD,
      revenuePercent: data.returnsValueShareCurrentFSD,
      returns: data.returnsValueCurrentFSD,
      revenuePerStore: data.revenuePerStoreCurrentFSD,
      missingDataText: 'revenue.noOpenStoresWithFloorDecrease',
    },
  ]

  const rows = []

  if (data.anyStoresNECom !== false) {
    rows.push(...stationaryData)
  }

  rows.push({
    icon: <i className="fm fm-at" />,
    count: data.storesCurrentECom,
    countActive: data.storesCurrentEComActive,
    revenue: data.revenueCurrentECom,
    revenueCompared: data.revenueComparedECom,
    revenuePercent: data.returnsValueShareCurrentECom,
    returns: data.returnsValueCurrentECom,
    missingDataText: 'revenue.noOnlineReportingStores',
  })
  const [_, sale, returns, saleWithoutReturns] = headers

  return (
    <div className="returns-value-details general-revenue_details">
      {description && <div className={classes.description}>{description}</div>}

      <Grid container>
        <Grid container>
          <Grid item xs={1} className={classes.header} />
          <Grid item xs={3} className={classNames(classes.header, classes.cell)}>
            {sale}
          </Grid>
          <Grid item xs={4} className={classNames(classes.header, classes.cell)}>
            {returns}
          </Grid>
          <Grid item xs={4} className={classes.header}>
            {saleWithoutReturns}
          </Grid>
        </Grid>

        {rows.map(rowData => {
          const hasReturns = rowData.returns || rowData.returns === 0
          const showRevenue = rowData.revenue || rowData.revenue > 0
          const missingDataText = getMissingDataText(rowData.missingDataText, rowData, translate)

          return (
            <Fragment key={rowData.icon.props.className}>
              <Box width="100%">
                <Divider />
              </Box>

              <Grid container>
                <Grid item xs={1} className={classes.icon}>
                  {rowData.icon}
                </Grid>

                {showRevenue ? (
                  <>
                    <Grid item xs={3} className={classNames(classes.value, classes.cell, classes.returnsValue)}>
                      {formatNumberWithCurrency({
                        currency: data.currency,
                        number: rowData.revenue,
                        translate,
                      })}
                    </Grid>

                    <Grid display="flex" item xs={4} className={classNames(classes.value, classes.cell, classes.returnsValue)}>
                      <Grid container item xs={8} justifyContent="flex-end">
                        {hasReturns &&
                          formatNumberWithCurrency({
                            currency: data.currency,
                            number: rowData.returns,
                            translate,
                          })}
                      </Grid>
                      <Grid container item xs={4}>
                        <InfoElement
                          className={classNames(classes.value, classes.percent, classes.marginLeft)}
                          percentDifference={getDifferenceWithPrecision({
                            handleNullCase: true,
                            number: rowData.revenuePercent,
                            precision: 0,
                            translate,
                          })}
                        />
                      </Grid>
                    </Grid>

                    <Grid item xs={4} className={classNames(classes.value, classes.returnsValue)}>
                      {hasReturns &&
                        formatNumberWithCurrency({
                          currency: data.currency,
                          number: rowData.revenue - rowData.returns,
                          translate,
                        })}
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={10} className={classes.missingData}>
                    {missingDataText}
                  </Grid>
                )}
              </Grid>
            </Fragment>
          )
        })}
        <Box width="100%">
          <Divider />
        </Box>
      </Grid>
    </div>
  )
}

export default ReturnsValueDetails
